/**
 * exporters — pure functions that turn app data into portable file formats.
 * No side effects; the caller pipes the string into exportFile()/shareText().
 */
import type { Session, Paper, ConferenceEvent } from '@conference/shared';
import type { SavedNote } from '../context/AppContext';

// ─── Markdown: single note ─────────────────────────────────────────────────────

export function noteToMarkdown(note: SavedNote): string {
  const lines: string[] = [];
  lines.push(`# ${note.title || 'Untitled Note'}`);
  lines.push('');
  const meta: string[] = [];
  if (note.sourceLabel) meta.push(note.sourceLabel);
  if (note.sessionTitle) meta.push(`Session: ${note.sessionTitle}`);
  if (note.paperTitle) meta.push(`Paper: ${note.paperTitle}`);
  try {
    meta.push(new Date(note.updatedAt).toLocaleString());
  } catch { /* ignore */ }
  if (meta.length) {
    lines.push(`_${meta.join(' · ')}_`);
    lines.push('');
  }
  lines.push(note.content || '');
  lines.push('');
  return lines.join('\n');
}

// ─── Markdown: full event export (notes + bookmarks + papers) ──────────────────

export function buildEventMarkdown(opts: {
  event?: ConferenceEvent | null;
  notes: SavedNote[];
  sessions: Session[];
  papers: Paper[];
}): string {
  const { event, notes, sessions, papers } = opts;
  const out: string[] = [];

  out.push(`# ${event?.name ?? 'Conference'} — My Export`);
  if (event) {
    out.push('');
    out.push(`${fmtDateRange(event.date_start, event.date_end)} · ${event.location ?? ''}`.trim());
  }
  out.push(`\n_Exported ${new Date().toLocaleString()}_\n`);

  // Bookmarked sessions (grouped by day, sorted by time)
  out.push('\n## Bookmarked Sessions\n');
  if (sessions.length === 0) {
    out.push('_None saved._');
  } else {
    const sorted = [...sessions].sort(
      (a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time),
    );
    let lastDate = '';
    for (const s of sorted) {
      if (s.date !== lastDate) {
        out.push(`\n### ${fmtDay(s.date)}\n`);
        lastDate = s.date;
      }
      const spk = s.speakers?.map((x) => x.profile?.name).filter(Boolean).join(', ');
      const bits = [
        `**${fmtTime(s.start_time)}–${fmtTime(s.end_time)}** — ${s.title}`,
        s.room ? `_${s.room}_` : '',
        spk ? `— ${spk}` : '',
      ].filter(Boolean);
      out.push(`- ${bits.join(' ')}`);
    }
  }

  // Favorited papers
  out.push('\n## Saved Papers\n');
  if (papers.length === 0) {
    out.push('_None saved._');
  } else {
    for (const p of papers) {
      const authors = Array.isArray(p.authors) ? p.authors.join(', ') : p.authors;
      out.push(`- **${p.title}**${authors ? ` — ${authors}` : ''}${p.conference ? ` (${p.conference})` : ''}`);
    }
  }

  // Notes
  out.push('\n## Notes\n');
  if (notes.length === 0) {
    out.push('_No notes yet._');
  } else {
    for (const n of notes) {
      out.push(`\n### ${n.title || 'Untitled Note'}`);
      const meta: string[] = [];
      if (n.sourceLabel) meta.push(n.sourceLabel);
      try { meta.push(new Date(n.updatedAt).toLocaleDateString()); } catch { /* ignore */ }
      if (meta.length) out.push(`_${meta.join(' · ')}_`);
      out.push('');
      out.push(n.content || '_empty_');
    }
  }

  return out.join('\n') + '\n';
}

// ─── BibTeX ────────────────────────────────────────────────────────────────────

export function buildBibTeX(papers: Paper[]): string {
  return papers.map(paperToBibTeX).join('\n\n') + '\n';
}

function paperToBibTeX(p: Paper): string {
  const authors = Array.isArray(p.authors) ? p.authors : p.authors ? [p.authors] : [];
  const year = paperYear(p);
  const key = citeKey(authors[0], year, p.title);
  const fields: [string, string][] = [
    ['title', p.title],
    ['author', authors.join(' and ')],
    ['booktitle', p.conference ?? ''],
    ['year', year ? String(year) : ''],
    ['doi', p.doi ?? ''],
    ['url', p.pdf_url ?? ''],
    ['note', p.paper_number ? `Paper ${p.paper_number}` : ''],
  ];
  const body = fields
    .filter(([, v]) => v)
    .map(([k, v]) => `  ${k} = {${bibEscape(v)}}`)
    .join(',\n');
  return `@inproceedings{${key},\n${body}\n}`;
}

function citeKey(firstAuthor: string | undefined, year: number | undefined, title: string): string {
  const last = (firstAuthor ?? 'anon').trim().split(/\s+/).pop() ?? 'anon';
  const word = title.trim().split(/\s+/)[0] ?? 'paper';
  const clean = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  return `${clean(last)}${year ?? ''}${clean(word)}`;
}

function bibEscape(s: string): string {
  return s.replace(/([&%$#_])/g, '\\$1');
}

// ─── RIS (Zotero / EndNote / Mendeley) ─────────────────────────────────────────

export function buildRIS(papers: Paper[]): string {
  return papers.map(paperToRIS).join('\n') + '\n';
}

function paperToRIS(p: Paper): string {
  const authors = Array.isArray(p.authors) ? p.authors : p.authors ? [p.authors] : [];
  const year = paperYear(p);
  const lines: string[] = ['TY  - CONF'];
  lines.push(`TI  - ${p.title}`);
  for (const a of authors) lines.push(`AU  - ${a}`);
  if (p.conference) lines.push(`T2  - ${p.conference}`);
  if (year) lines.push(`PY  - ${year}`);
  if (p.abstract) lines.push(`AB  - ${p.abstract.replace(/\r?\n/g, ' ')}`);
  if (p.doi) lines.push(`DO  - ${p.doi}`);
  if (p.pdf_url) lines.push(`UR  - ${p.pdf_url}`);
  lines.push('ER  - ');
  return lines.join('\n');
}

// ─── iCalendar (.ics) ──────────────────────────────────────────────────────────

/**
 * Build an .ics calendar from sessions. Emits floating local times (no TZID),
 * which calendar apps interpret in the user's local timezone — correct for an
 * attendee physically at the conference. The event timezone is noted in the
 * DESCRIPTION for clarity.
 */
export function buildICS(sessions: Session[], event?: ConferenceEvent | null): string {
  const now = icsStamp(new Date());
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Conference App//Agenda Export//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];
  if (event?.name) lines.push(foldLine(`X-WR-CALNAME:${icsEscape(event.name)}`));
  if (event?.timezone) lines.push(`X-WR-TIMEZONE:${icsEscape(event.timezone)}`);

  for (const s of sessions) {
    const dtStart = icsLocal(s.date, s.start_time);
    const dtEnd = icsLocal(s.date, s.end_time);
    if (!dtStart || !dtEnd) continue;
    const speakers = s.speakers?.map((x) => x.profile?.name).filter(Boolean).join(', ');
    const descParts = [
      s.description ?? '',
      speakers ? `Speakers: ${speakers}` : '',
      s.track ? `Track: ${s.track}` : '',
      event?.timezone ? `Times shown in event timezone (${event.timezone}).` : '',
    ].filter(Boolean);

    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${s.id}@conference-app`);
    lines.push(`DTSTAMP:${now}`);
    lines.push(`DTSTART:${dtStart}`);
    lines.push(`DTEND:${dtEnd}`);
    lines.push(foldLine(`SUMMARY:${icsEscape(s.title)}`));
    if (s.room || s.location) lines.push(foldLine(`LOCATION:${icsEscape(s.room ?? s.location ?? '')}`));
    if (descParts.length) lines.push(foldLine(`DESCRIPTION:${icsEscape(descParts.join('\\n'))}`));
    lines.push('END:VEVENT');
  }

  lines.push('END:VCALENDAR');
  return lines.join('\r\n') + '\r\n';
}

// ─── Formatting helpers ────────────────────────────────────────────────────────

function paperYear(p: Paper): number | undefined {
  if (p.year) return p.year;
  const src = p.created_at;
  if (src) {
    const y = new Date(src).getFullYear();
    if (!Number.isNaN(y)) return y;
  }
  return undefined;
}

function icsLocal(date: string, time: string): string | null {
  // date = YYYY-MM-DD, time = HH:MM[:SS] → YYYYMMDDTHHMMSS (floating local)
  if (!date || !time) return null;
  const d = date.replace(/-/g, '');
  const t = time.split(':');
  if (t.length < 2) return null;
  const hh = t[0].padStart(2, '0');
  const mm = t[1].padStart(2, '0');
  const ss = (t[2] ?? '00').padStart(2, '0');
  return `${d}T${hh}${mm}${ss}`;
}

function icsStamp(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

function icsEscape(s: string): string {
  return s
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

/** Fold lines longer than 75 octets per RFC 5545 (simple char-based fold). */
function foldLine(line: string): string {
  if (line.length <= 75) return line;
  const chunks: string[] = [];
  let rest = line;
  chunks.push(rest.slice(0, 75));
  rest = rest.slice(75);
  while (rest.length > 74) {
    chunks.push(' ' + rest.slice(0, 74));
    rest = rest.slice(74);
  }
  if (rest.length) chunks.push(' ' + rest);
  return chunks.join('\r\n');
}

function fmtTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmtDay(dateStr: string): string {
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long', month: 'short', day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function fmtDateRange(start: string, end: string): string {
  try {
    const s = new Date(start);
    const e = new Date(end);
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${e.toLocaleDateString('en-US', { day: 'numeric' })}, ${s.getFullYear()}`;
  } catch {
    return `${start} – ${end}`;
  }
}
